import React, { useState, useEffect } from "react";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import { makeStyles, Paper, Tabs, Tab } from "@material-ui/core";

import TabPanel from "../../components/TabPanel";

import SchedulesForm from "../../components/SchedulesForm";
import CompaniesManager from "../../components/CompaniesManager";
import PlansManager from "../../components/PlansManager";
import HelpsManager from "../../components/HelpsManager";
import Options from "../../components/Settings/Options";

import { i18n } from "../../translate/i18n.js";
import { toast } from "react-toastify";

import useCompanies from "../../hooks/useCompanies";
import useAuth from "../../hooks/useAuth.js";
import useSettings from "../../hooks/useSettings";

import OnlyForSuperUser from "../../components/OnlyForSuperUser";

const useStyles = makeStyles((theme) => ({
  root: {
    flex: 1,
    backgroundColor: theme.palette.background.paper,
  },
  mainPaper: {
    ...theme.scrollbarStyles,
    overflowY: "scroll",
    flex: 1,
  },
  tab: {
    backgroundColor: theme.palette.options,
    borderRadius: 4,
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
  },
  paper: {
    ...theme.scrollbarStyles,
    padding: theme.spacing(3),
    width: "100%",
  },
}));

const SettingsCustom = () => {
  const classes = useStyles();
  const [tab, setTab] = useState("options");
  const [schedules, setSchedules] = useState([]);
  const [company, setCompany] = useState({});
  const [loading, setLoading] = useState(false);
  const [currentUser, setCurrentUser] = useState({});
  const [settings, setSettings] = useState([]);
  const [schedulesEnabled, setSchedulesEnabled] = useState(false);

  const { getCurrentUserInfo } = useAuth();
  const { find, updateSchedules } = useCompanies();
  const { getAll: getAllSettings } = useSettings();

  // Carrega dados apenas uma vez
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const companyId = localStorage.getItem("companyId");
        const [companyData, settingsList, userData] = await Promise.all([
          find(companyId),
          getAllSettings(),
          getCurrentUserInfo(),
        ]);

        setCompany(companyData);
        setSchedules(companyData.schedules || []);
        setSettings(settingsList);
        setCurrentUser(userData);

        if (Array.isArray(settingsList)) {
          const scheduleType = settingsList.find((s) => s.key === "scheduleType");
          const isEnabled = scheduleType ? scheduleType.value === "company" : false;
          setSchedulesEnabled(isEnabled);

          // Se horários estiverem habilitados, define a aba correta
          if (isEnabled && tab === "options") {
            // Não muda a aba, mas garante que está pronto
          }
        }
      } catch (err) {
        toast.error("Erro ao carregar configurações.");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTabChange = (event, newValue) => {
    if (["options", "schedules", "companies", "plans", "helps"].includes(newValue)) {
      // Só permite mudar para "schedules" se estiver habilitado
      if (newValue === "schedules" && !schedulesEnabled) {
        return;
      }
      setTab(newValue);
    }
  };

  const handleSubmitSchedules = async (data) => {
    setLoading(true);
    try {
      setSchedules(data);
      await updateSchedules({ id: company.id, schedules: data });
      toast.success("Horários atualizados com sucesso.");
    } catch (err) {
      toast.error("Erro ao salvar horários.");
    }
    setLoading(false);
  };

  const isSuper = () => currentUser.super;

  return (
    <MainContainer className={classes.root}>
      <MainHeader>
        <Title>{i18n.t("settings.title")}</Title>
      </MainHeader>

      <Paper className={classes.mainPaper} elevation={1}>
        {/* Abas principais */}
        <Tabs
          value={tab}
          indicatorColor="primary"
          textColor="primary"
          scrollButtons="on"
          variant="scrollable"
          onChange={handleTabChange}
          className={classes.tab}
        >
          <Tab label="Opções" value="options" />
          
          {/* Sempre renderiza o Tab, mas oculta se desabilitado */}
          <Tab
            label="Horários"
            value="schedules"
            disabled={!schedulesEnabled}
            style={{ display: schedulesEnabled ? "flex" : "none" }}
          />
          
          {isSuper() && <Tab label="Empresas" value="companies" />}
          {isSuper() && <Tab label="Planos" value="plans" />}
          {isSuper() && <Tab label="Ajuda" value="helps" />}
        </Tabs>

        {/* Conteúdo das abas */}
        <Paper className={classes.paper} elevation={0}>
          <TabPanel value={tab} name="options">
            <Options
              settings={settings}
              scheduleTypeChanged={(value) => {
                //setSchedulesEnabled(value === "company");
                setSchedulesEnabled(true); //deixado sempre true 
                // Se desabilitar, volta para "options"
                if (!value && tab === "schedules") {
                  setTab("options");
                }
              }}
            />
          </TabPanel>

          {/* Só renderiza o conteúdo se habilitado */}
          {schedulesEnabled && (
            <TabPanel value={tab} name="schedules">
              <SchedulesForm
                loading={loading}
                onSubmit={handleSubmitSchedules}
                initialValues={schedules}
              />
            </TabPanel>
          )}

          <OnlyForSuperUser
            user={currentUser}
            yes={() => (
              <TabPanel value={tab} name="companies">
                <CompaniesManager />
              </TabPanel>
            )}
          />

          <OnlyForSuperUser
            user={currentUser}
            yes={() => (
              <TabPanel value={tab} name="plans">
                <PlansManager />
              </TabPanel>
            )}
          />

          <OnlyForSuperUser
            user={currentUser}
            yes={() => (
              <TabPanel value={tab} name="helps">
                <HelpsManager />
              </TabPanel>
            )}
          />
        </Paper>
      </Paper>
    </MainContainer>
  );
};

export default SettingsCustom;